import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { t, errorText } from '../i18n/index.ts';
import { api } from '../lib/api.ts';
import { useSession } from '../lib/session.tsx';
import { useToast } from '../components/ui.tsx';
import { currentSubscription, disablePush, enablePush, pushSupport } from '../lib/push.ts';

const CATEGORIES = ['turn', 'reminder', 'autoAction', 'tournament', 'results', 'payments'] as const;
type Category = (typeof CATEGORIES)[number];
type Channel = 'push' | 'email';

/** Device notifications for this browser + per-category channel choices. */
export function NotificationSettings() {
  const { me, refresh } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const support = pushSupport();
  const [thisDevice, setThisDevice] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const devices = useQuery({ queryKey: ['push-devices'], queryFn: () => api.get<{ devices: number }>('/api/me/push') });
  const permission = support === 'supported' ? Notification.permission : 'default';

  useEffect(() => {
    void currentSubscription().then((s) => setThisDevice(!!s));
  }, []);

  if (!me) return null;
  const prefs = me.notifyPrefs as Record<Category, { email: boolean; push: boolean }>;

  const save = async (category: Category, channel: Channel, value: boolean) => {
    try {
      await api.patch('/api/me', { notifyPrefs: { [category]: { [channel]: value } } });
      await refresh();
    } catch (err) {
      toast(errorText(err), 'error');
    }
  };

  const enable = async () => {
    setBusy(true);
    try {
      const result = await enablePush();
      if (result === 'enabled') {
        setThisDevice(true);
        toast(t('push.enabled'));
      } else if (result === 'denied') toast(t('push.denied'), 'error');
      else toast(t('push.unsupported'), 'error');
    } catch (err) {
      toast(`${t('push.failed')} ${errorText(err)}`, 'error');
    } finally {
      setBusy(false);
      void qc.invalidateQueries({ queryKey: ['push-devices'] });
    }
  };

  const disable = async () => {
    setBusy(true);
    await disablePush();
    setThisDevice(false);
    setBusy(false);
    void qc.invalidateQueries({ queryKey: ['push-devices'] });
  };

  const test = async () => {
    try {
      await api.post('/api/me/push/test');
      toast(t('push.testSent'));
    } catch (err) {
      toast(errorText(err), 'error');
    }
  };

  return (
    <section className="panel" aria-labelledby="notify-h">
      <h2 id="notify-h">{t('profile.notifyTitle')}</h2>
      <p className="hint">{t('profile.notifyHelp')}</p>

      <div className="device-card">
        <div>
          <strong>{t('push.thisDevice')}</strong>
          <p className="hint" style={{ margin: '2px 0 0' }}>
            {support === 'needs-install'
              ? t('push.needsInstall')
              : support === 'unsupported'
                ? t('push.unsupported')
                : permission === 'denied'
                  ? t('push.blocked')
                  : thisDevice
                    ? t('push.onHere')
                    : t('push.offHere')}
          </p>
          {!!devices.data?.devices && <p className="hint" style={{ margin: '2px 0 0' }}>{t('push.deviceCount', { n: devices.data.devices })}</p>}
        </div>
        <div className="btn-row">
          {support === 'supported' && permission !== 'denied' && !thisDevice && (
            <button className="btn btn-primary btn-small" disabled={busy} onClick={enable}>
              {t('push.enable')}
            </button>
          )}
          {thisDevice && (
            <>
              <button className="btn btn-small" onClick={test}>
                {t('push.test')}
              </button>
              <button className="btn btn-ghost btn-small" disabled={busy} onClick={disable}>
                {t('push.disable')}
              </button>
            </>
          )}
        </div>
      </div>

      <table className="data-table prefs-table">
        <caption className="visually-hidden">{t('profile.notifyTitle')}</caption>
        <thead>
          <tr>
            <th scope="col">{t('push.whatHappens')}</th>
            <th scope="col" className="c">
              {t('push.device')}
              {permission === 'denied' && <span aria-hidden="true">*</span>}
            </th>
            <th scope="col" className="c">
              {t('push.email')}
            </th>
          </tr>
        </thead>
        <tbody>
          {CATEGORIES.map((c) => (
            <tr key={c}>
              <th scope="row">{t(`profile.prefs.${c}`)}</th>
              {(['push', 'email'] as const).map((ch) => (
                <td key={ch} className="c">
                  <label className="switch">
                    <input
                      type="checkbox"
                      role="switch"
                      checked={prefs[c][ch]}
                      aria-label={`${t(`profile.prefs.${c}`)}: ${ch === 'push' ? t('push.device') : t('push.email')}`}
                      onChange={(e) => void save(c, ch, e.target.checked)}
                    />
                    <span className="switch-track" aria-hidden="true" />
                  </label>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {permission === 'denied' && <p className="hint">{t('push.blockedFootnote')}</p>}
      <p className="hint">{t('push.inAppAlways')}</p>
    </section>
  );
}
